import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { dismissWhatsNew } from "@/features/admin/api";
import { useAnnouncements } from "@/features/admin/hooks/use-announcements";
import { SEVERITY_BANNER_CLASS } from "@/features/admin/lib/severity";
import { cn } from "@/lib/utils";

/**
 * Firm-facing consumer of vendor announcements: the What's New login popup and
 * the live broadcast banner (both short-polled). Rendered inside the app shell
 * for firm users. Events/Resources are surfaced on the dashboard, not here.
 */
export function AnnouncementsCenter() {
  const { announcements, refresh } = useAnnouncements();
  const [popupOpen, setPopupOpen] = useState(false);
  // Track which publish we've locally dismissed so the popup doesn't flash back
  // between the dismiss POST and the next poll.
  const [dismissed, setDismissed] = useState<string | null>(null);

  const whatsnew = announcements?.whatsnew ?? null;

  useEffect(() => {
    if (whatsnew && whatsnew.publishedAt !== dismissed) setPopupOpen(true);
  }, [whatsnew, dismissed]);

  async function handleDismiss() {
    setPopupOpen(false);
    if (whatsnew) setDismissed(whatsnew.publishedAt);
    try {
      await dismissWhatsNew();
      await refresh();
    } catch {
      // If it fails the popup simply reappears on the next login — acceptable.
    }
  }

  const broadcast = announcements?.broadcast ?? null;

  return (
    <>
      {broadcast && (
        <div
          role="status"
          className={cn(
            "mb-4 rounded-md border px-4 py-2.5 text-sm font-medium",
            SEVERITY_BANNER_CLASS[broadcast.severity],
          )}
        >
          {broadcast.message}
        </div>
      )}

      <Dialog open={popupOpen} onOpenChange={(o) => (o ? setPopupOpen(true) : void handleDismiss())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>What's New</DialogTitle>
          </DialogHeader>
          {whatsnew && <SanitizedHtml html={whatsnew.current} />}
          <DialogFooter>
            <Button onClick={() => void handleDismiss()}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
